import { getRouteApi } from "@tanstack/react-router"
import { useStore } from "zustand"
import { createStore, type StoreApi } from "zustand/vanilla"

import type { DailyContentKind } from "@/core/daily-content/kinds"
import type {
  DailyContentFallbackMode,
  DailyContentProfile,
} from "@/lib/daily-content-api"

export type ProfileDraft = {
  name: string
  kind: DailyContentKind
  targetChannelId: string
  scheduleValue: string
  fallbackMode: DailyContentFallbackMode
  pingRoleId: string
  pinMessage: boolean
  createThread: boolean
}

type CalendarViewMode = "month" | "week"

type DailyContentUiState = {
  // New profile draft (HIGH CHURN - causes rerenders on every keystroke)
  newProfileDraft: ProfileDraft

  // Per-profile drafts (HIGH CHURN - keyed by profile ID)
  profileDrafts: Record<string, ProfileDraft>

  // Calendar state (toggles cause rerenders)
  calendarView: CalendarViewMode
  calendarDate: Date
  isCalendarOpen: boolean

  // Page messages
  pageError: string | null

  // Offline status
  isOffline: boolean
}

const EMPTY_PROFILE_DRAFT: ProfileDraft = {
  name: "",
  kind: "question",
  targetChannelId: "",
  scheduleValue: "0 9 * * *",
  fallbackMode: "none",
  pingRoleId: "",
  pinMessage: false,
  createThread: false,
}

const buildProfileDraft = (profile: DailyContentProfile): ProfileDraft => ({
  name: profile.name,
  kind: profile.kind,
  targetChannelId: profile.targetChannelId,
  scheduleValue: profile.scheduleValue,
  fallbackMode: profile.fallbackMode,
  pingRoleId: profile.pingRoleId ?? "",
  pinMessage: profile.pinMessage,
  createThread: profile.createThread,
})

const buildInitialProfileDrafts = (
  profiles: DailyContentProfile[],
): Record<string, ProfileDraft> => {
  return Object.fromEntries(
    profiles.map((profile) => [profile.id, buildProfileDraft(profile)]),
  )
}

type DailyContentStoreActions = {
  // New profile draft actions
  setNewProfileDraft: (draft: ProfileDraft) => void
  updateNewProfileDraftField: <K extends keyof ProfileDraft>(
    key: K,
    value: ProfileDraft[K],
  ) => void
  resetNewProfileDraft: () => void

  // Profile draft actions
  setProfileDrafts: (drafts: Record<string, ProfileDraft>) => void
  setProfileDraft: (profileId: string, draft: ProfileDraft) => void
  updateProfileDraftField: <K extends keyof ProfileDraft>(
    profileId: string,
    key: K,
    value: ProfileDraft[K],
  ) => void
  resetProfileDraft: (profileId: string, profile: DailyContentProfile) => void

  // Calendar actions
  setCalendarView: (view: CalendarViewMode) => void
  setCalendarDate: (date: Date) => void
  setIsCalendarOpen: (open: boolean) => void

  // Page message actions
  setPageError: (error: string | null) => void

  // Offline status actions
  setIsOffline: (offline: boolean) => void

  // Sync actions
  syncProfileDrafts: (profiles: DailyContentProfile[]) => void
}

export type DailyContentUiStore = StoreApi<
  DailyContentUiState & DailyContentStoreActions
>

export const createDailyContentUiStore = () =>
  createStore<DailyContentUiState & DailyContentStoreActions>((set) => ({
    newProfileDraft: {
      name: "",
      kind: "question",
      targetChannelId: "",
      scheduleValue: "0 9 * * *",
      fallbackMode: "none",
      pingRoleId: "",
      pinMessage: false,
      createThread: false,
    },
    profileDrafts: {},
    calendarView: "month",
    calendarDate: new Date(),
    isCalendarOpen: false,
    pageError: null,
    isOffline: false,

    // New profile draft actions
    setNewProfileDraft: (draft) => set({ newProfileDraft: draft }),
    updateNewProfileDraftField: (key, value) =>
      set((state) => ({
        newProfileDraft: { ...state.newProfileDraft, [key]: value },
      })),
    resetNewProfileDraft: () =>
      set({
        newProfileDraft: {
          name: "",
          kind: "question",
          targetChannelId: "",
          scheduleValue: "0 9 * * *",
          fallbackMode: "none",
          pingRoleId: "",
          pinMessage: false,
          createThread: false,
        },
      }),

    // Profile draft actions
    setProfileDrafts: (drafts) => set({ profileDrafts: drafts }),
    setProfileDraft: (profileId, draft) =>
      set(
        (state) =>
          ({
            profileDrafts: { ...state.profileDrafts, [profileId]: draft },
          }) as Partial<DailyContentUiState & DailyContentStoreActions>,
      ),
    updateProfileDraftField: (profileId, key, value) =>
      set((state) => ({
        profileDrafts: {
          ...state.profileDrafts,
          [profileId]: {
            ...EMPTY_PROFILE_DRAFT,
            ...state.profileDrafts[profileId],
            [key]: value,
          },
        },
      })),
    resetProfileDraft: (profileId, profile) =>
      set(
        (state) =>
          ({
            profileDrafts: {
              ...state.profileDrafts,
              [profileId]: buildProfileDraft(profile),
            },
          }) as Partial<DailyContentUiState & DailyContentStoreActions>,
      ),

    // Calendar actions
    setCalendarView: (view) => set({ calendarView: view }),
    setCalendarDate: (date) => set({ calendarDate: date }),
    setIsCalendarOpen: (open) => set({ isCalendarOpen: open }),

    // Page message actions
    setPageError: (error) => set({ pageError: error }),

    // Offline status actions
    setIsOffline: (offline) => set({ isOffline: offline }),

    // Sync actions
    syncProfileDrafts: (profiles) =>
      set((state) => {
        const next: Record<string, ProfileDraft> = {}
        for (const profile of profiles) {
          next[profile.id] =
            state.profileDrafts[profile.id] ?? buildProfileDraft(profile)
        }
        return { profileDrafts: next } as Partial<
          DailyContentUiState & DailyContentStoreActions
        >
      }),
  }))

// Selector hooks for fine-grained subscriptions
export const useNewProfileDraft = (store: DailyContentUiStore) =>
  useStore(store, (s) => s.newProfileDraft)

export const useProfileDraft = (
  store: DailyContentUiStore,
  profileId: string,
) => useStore(store, (s) => s.profileDrafts[profileId])

export const useCalendarView = (store: DailyContentUiStore) =>
  useStore(store, (s) => s.calendarView)

export const useCalendarDate = (store: DailyContentUiStore) =>
  useStore(store, (s) => s.calendarDate)

export const useIsCalendarOpen = (store: DailyContentUiStore) =>
  useStore(store, (s) => s.isCalendarOpen)

export const usePageError = (store: DailyContentUiStore) =>
  useStore(store, (s) => s.pageError)

export const useIsOffline = (store: DailyContentUiStore) =>
  useStore(store, (s) => s.isOffline)

// Route-scoped access
const dailyContentRouteApi = getRouteApi("/_authed/_admin/daily-content")

export const useDailyContentRouteStore = () =>
  dailyContentRouteApi.useRouteContext({
    select: (ctx) => ctx.dailyContentStore,
  })
